/**
 * Unified tracking helpers for Google Tag Manager (dataLayer) and Microsoft
 * Clarity — thin, defensive wrappers used by the lead-capture forms and the
 * consent banner.
 *
 * Why a single fan-out layer?
 *   The published GTM container listens for a dataLayer push with an `event`
 *   key and fires the GA4 event from there. We do not call `gtag('event', ...)`
 *   directly, because doing so alongside GTM would double-count every lead.
 *   Every conversion hits two independent surfaces:
 *     1. window.dataLayer.push({ event, ...params })   ← GTM → GA4
 *     2. window.clarity('set', 'conversion', name) +
 *        window.clarity('upgrade', name)               ← Clarity flags
 *
 *   gtag is still used for consent state (gtag('consent', 'update', ...)) and
 *   for GA4 user properties (gtag('set', 'user_properties', ...)) because
 *   neither of those flows through GTM's Custom Event triggers.
 *
 * Each surface is wrapped in its own try/catch. One vendor being broken,
 * blocked, or unloaded must never break the user flow, and must never
 * stop the other from firing.
 *
 * PII discipline
 *   GA4's terms (and POPIA / GDPR) forbid sending personally identifiable
 *   information (email, full name, phone, etc.) as event parameters or
 *   user properties. The same rule is applied to the dataLayer and to
 *   Clarity via the ALLOWED_USER_PROPS allow-list on setUserProperties,
 *   and by the calling components never passing raw email / name / phone
 *   into trackEvent params. Callers pass boolean flags (has_company,
 *   has_phone) instead of the raw values.
 */

const CONSENT_KEY = "radius_consent";

const safeGtag = () => (typeof window !== "undefined" ? window.gtag : null);
const safeClarity = () =>
  typeof window !== "undefined" ? window.clarity : null;
const safeDataLayer = () => {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * GA4 user_property names must be alphanumeric + underscore, ≤24 chars.
 * We allow-list keys explicitly so a future caller cannot accidentally
 * leak PII by passing the whole formData.
 */
const ALLOWED_USER_PROPS = new Set([
  "company_name",
  "source",
  "last_service_enquired",
  "last_resource_downloaded",
]);

/**
 * Persist a consent decision and notify every downstream vendor.
 * Safe to call before gtag / clarity have loaded — each vendor call is
 * independently guarded.
 */
export const setConsent = ({ analytics, marketing } = {}) => {
  const analyticsGranted = Boolean(analytics);
  const marketingGranted = Boolean(marketing);
  const record = {
    analytics: analyticsGranted,
    marketing: marketingGranted,
    updated_at: new Date().toISOString(),
  };

  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    }
  } catch {
    /* tracking never breaks the user flow */
  }

  try {
    if (typeof window !== "undefined") {
      window.__radiusConsent = record;
    }
  } catch {
    /* no-op */
  }

  try {
    const gtag = safeGtag();
    if (typeof gtag === "function") {
      gtag("consent", "update", {
        ad_storage: marketingGranted ? "granted" : "denied",
        ad_user_data: marketingGranted ? "granted" : "denied",
        ad_personalization: marketingGranted ? "granted" : "denied",
        analytics_storage: analyticsGranted ? "granted" : "denied",
      });
    }
  } catch {
    /* no-op */
  }

  try {
    const clarity = safeClarity();
    if (typeof clarity === "function") {
      // Clarity's consent v2 keys are case-sensitive.
      clarity("consentv2", {
        ad_Storage: marketingGranted ? "granted" : "denied",
        analytics_Storage: analyticsGranted ? "granted" : "denied",
      });
    }
  } catch {
    /* no-op */
  }

  try {
    const dl = safeDataLayer();
    if (dl) {
      dl.push({
        event: "consent_update",
        consent_analytics: analyticsGranted,
        consent_marketing: marketingGranted,
      });
    }
  } catch {
    /* no-op */
  }
};

/**
 * Set non-PII user properties on GA4. Behaviour is identical to the
 * legacy gaSetUserProperties helper. The allow-list is unchanged.
 */
export const setUserProperties = (props) => {
  const gtag = safeGtag();
  if (typeof gtag !== "function" || !props) return;
  const clean = Object.fromEntries(
    Object.entries(props).filter(
      ([k, v]) =>
        ALLOWED_USER_PROPS.has(k) && v !== undefined && v !== null && v !== ""
    )
  );
  if (Object.keys(clean).length === 0) return;
  try {
    gtag("set", "user_properties", clean);
  } catch {
    /* tracking never breaks the user flow */
  }
};

/**
 * RFC 4122 v4 UUID, generated without an external dependency.
 * Prefers crypto.randomUUID when available.
 */
export const generateEventId = () => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to manual generator */
  }
  // RFC 4122 §4.4 manual fallback.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Fan a single conversion event out to GTM's dataLayer and Clarity. GTM is
 * responsible for firing the GA4 event from the dataLayer push, so we do not
 * call gtag('event', ...) here — that would double-count every conversion.
 * Each vendor call is independently wrapped so one failure cannot block the
 * other.
 */
export const trackEvent = (name, params = {}) => {
  if (!name || typeof name !== "string") return;

  // Strip empty values so vendors don't record "undefined" strings.
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );

  // (a) GTM dataLayer — GTM listens for this push and fires the GA4 event.
  try {
    const dl = safeDataLayer();
    if (dl) {
      dl.push({ event: name, ...cleanParams });
    }
  } catch {
    /* no-op */
  }

  // (b) Microsoft Clarity — flag session as a conversion and tag it.
  try {
    const clarity = safeClarity();
    if (typeof clarity === "function") {
      clarity("set", "conversion", name);
      clarity("upgrade", name);
    }
  } catch {
    /* no-op */
  }
};
